/**
 * Clinician AI Practice & Schedule Copilot Widget
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Clock, FileSignature } from "lucide-react";
import { useLocation, useParams } from "react-router-dom";
import { GlowingAiChatAssistant } from "@/components/ui/glowing-ai-chat-assistant";
import {
  CopilotChatMessages,
  type CopilotChatMessage,
} from "@/components/shared/copilot-chat-messages";
import { FormatCopilotText } from "@/components/shared/format-copilot-text";
import apiClient from "@/api/client";
import { useAuth } from "@/hooks/use-auth";
import {
  clearCopilotChat,
  loadCopilotChat,
  saveCopilotChat,
} from "@/lib/copilot-chat-storage";

const FALLBACK_GREETING =
  "Hello! I'm your clinical AI assistant. Ask me about clinical topics, billing and coding, your organization, today's schedule, or anything on the current patient chart.";

function nowLabel() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function greetingMessage(text: string, stream = false): CopilotChatMessage {
  return {
    id: `greeting_${Date.now()}`,
    sender: "copilot",
    text,
    timestamp: nowLabel(),
    stream,
  };
}

export function ClinicianCopilotWidget({
  enabled = true,
  onToggleEnabled,
}: {
  enabled?: boolean;
  onToggleEnabled?: (enabled: boolean) => void;
}) {
  const { user } = useAuth();
  const location = useLocation();
  const params = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<CopilotChatMessage[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const briefingDoneRef = useRef(false);
  const briefingLoadingRef = useRef(false);

  const patientId =
    (params.id && location.pathname.includes("/patients/") ? params.id : undefined) ||
    location.pathname.match(/\/patients\/([^/]+)/)?.[1];
  const encounterId =
    (params.id && location.pathname.includes("/encounters/") ? params.id : undefined) ||
    location.pathname.match(/\/encounters\/([^/]+)/)?.[1];

  const pageContext = location.pathname;

  const buildPayload = useCallback(
    (text?: string, briefing = false, recentMessages: string[] = []) => ({
      query: text ?? "",
      page: pageContext,
      patient_id: patientId,
      encounter_id: encounterId,
      briefing,
      recent_messages: recentMessages.slice(-6),
    }),
    [pageContext, patientId, encounterId],
  );

  useEffect(() => {
    if (!user?.id) {
      setMessages([]);
      setHydrated(false);
      briefingDoneRef.current = false;
      return;
    }

    const stored = loadCopilotChat(user.id);
    if (stored && stored.length > 0) {
      setMessages(stored);
      briefingDoneRef.current = true;
    } else {
      setMessages([]);
      briefingDoneRef.current = false;
    }
    setHydrated(true);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !hydrated) return;
    if (messages.length === 0) return;
    saveCopilotChat(user.id, messages);
  }, [messages, user?.id, hydrated]);

  const fetchBriefing = useCallback(async () => {
    if (briefingLoadingRef.current || briefingDoneRef.current) return;
    briefingLoadingRef.current = true;
    setLoading(true);

    try {
      const { data } = await apiClient.post("/ai/chat/provider", buildPayload("", true));
      const payload = data?.data ?? data;
      setMessages([greetingMessage(payload?.answer || FALLBACK_GREETING, true)]);
      briefingDoneRef.current = true;
    } catch {
      setMessages([greetingMessage(FALLBACK_GREETING, false)]);
      briefingDoneRef.current = true;
    } finally {
      briefingLoadingRef.current = false;
      setLoading(false);
    }
  }, [buildPayload]);

  useEffect(() => {
    if (!isOpen || !hydrated || !user?.id) return;
    if (messages.length > 0) return;
    void fetchBriefing();
  }, [isOpen, hydrated, user?.id, messages.length, fetchBriefing]);

  function markStreamDone(id: string) {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, stream: false } : m)));
  }

  const handleSend = async (customQuery?: string) => {
    const textToSend = (customQuery ?? query).trim();
    if (!textToSend || loading) return;

    const userMsg: CopilotChatMessage = {
      id: `user_${Date.now()}`,
      sender: "user",
      text: textToSend,
      timestamp: nowLabel(),
    };

    setMessages((prev) => [...prev, userMsg]);
    if (!customQuery) setQuery("");
    setLoading(true);

    const recentForContext = [...messages, userMsg]
      .slice(-6)
      .map((m) => `${m.sender === "user" ? "User" : "Assistant"}: ${m.text}`);

    try {
      const { data } = await apiClient.post("/ai/chat/provider", buildPayload(textToSend, false, recentForContext));
      const payload = data?.data ?? data;
      const copilotMsg: CopilotChatMessage = {
        id: `ai_${Date.now()}`,
        sender: "copilot",
        text: payload?.answer || "I'm here to help with clinical, billing, and practice questions.",
        timestamp: nowLabel(),
        appointments: payload?.appointments_found,
        stream: true,
      };
      setMessages((prev) => [...prev, copilotMsg]);
    } catch (err: unknown) {
      const errDetail =
        (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ||
        (err as Error)?.message ||
        "Unable to reach AI service.";
      setMessages((prev) => [
        ...prev,
        {
          id: `ai_${Date.now()}`,
          sender: "copilot",
          text: `I couldn't reach the AI service right now. ${errDetail}`,
          timestamp: nowLabel(),
          stream: false,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = () => {
    if (user?.id) clearCopilotChat(user.id);
    briefingDoneRef.current = false;
    briefingLoadingRef.current = false;
    setMessages([]);
    setQuery("");
    void fetchBriefing();
  };

  // Start screen until the first question: the briefing is the welcome text.
  const showIntro = !messages.some((m) => m.sender === "user");
  const greeting = messages.find((m) => m.sender === "copilot")?.text;

  return (
    <GlowingAiChatAssistant
      enabled={enabled}
      open={isOpen}
      onOpenChange={setIsOpen}
      message={query}
      onMessageChange={setQuery}
      onSend={() => handleSend()}
      disclaimer="AI-generated — verify in the chart before acting on it."
      onNewChat={handleNewChat}
      loading={loading}
      title="Clinical AI Assistant"
      statusLabel={loading ? "Thinking…" : patientId ? "Schedule, this chart & coding" : "Schedule, notes & coding"}
      onDisable={
        onToggleEnabled
          ? () => {
              setIsOpen(false);
              onToggleEnabled(false);
            }
          : undefined
      }
      showIntro={showIntro}
      intro={
        greeting ? (
          <FormatCopilotText text={greeting} />
        ) : loading ? (
          "Getting your briefing…"
        ) : (
          FALLBACK_GREETING
        )
      }
      suggestions={[
        { label: "Next appointment", icon: Clock, onSelect: () => void handleSend("When is my next appointment?") },
        { label: "Unsigned notes", icon: FileSignature, onSelect: () => void handleSend("Which notes are unsigned?") },
      ]}
    >
      <CopilotChatMessages
        messages={messages}
        loading={loading}
        onStreamComplete={markStreamDone}
      />
    </GlowingAiChatAssistant>
  );
}
